import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowArcLeft, ArrowUpRight, Circle, PencilSimple, Trash } from "@phosphor-icons/react";
import { api } from "../api/client";
import type { Foto, Zeichen } from "../api/types";
import { Meldung } from "./Bausteine";

/**
 * Pfeile, Kreise und Freihand auf einem Foto (SPEC §5.8). Gezeichnet wird auf der
 * unmarkierten Vorschau; gespeichert werden die Formen (relativ 0..1, zum Weiterbearbeiten)
 * und die fertige Fassung als JPEG. Das Original bleibt unberührt.
 */
const FARBEN = ["#e0221a", "#ffd400", "#ffffff"];
type Werkzeug = Zeichen["art"];

function zeichnen(ctx: CanvasRenderingContext2D, w: number, h: number, z: Zeichen) {
  const p = z.punkte.map(([x, y]) => [x * w, y * h] as const);
  const dicke = Math.max(3, w * 0.006);
  ctx.strokeStyle = z.farbe;
  ctx.fillStyle = z.farbe;
  ctx.lineWidth = dicke;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Dunkler Schatten: rot auf rotem Klinker bleibt so sichtbar.
  ctx.shadowColor = "rgba(0,0,0,.45)";
  ctx.shadowBlur = dicke;
  if (z.art === "linie") {
    ctx.beginPath();
    p.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  } else if (p.length >= 2) {
    const [[x1, y1], [x2, y2]] = [p[0]!, p[p.length - 1]!];
    if (z.art === "kreis") {
      ctx.beginPath();
      ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2 || 1, Math.abs(y2 - y1) / 2 || 1, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      const winkel = Math.atan2(y2 - y1, x2 - x1);
      const spitze = dicke * 4.5;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2 - Math.cos(winkel) * spitze * 0.6, y2 - Math.sin(winkel) * spitze * 0.6);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - spitze * Math.cos(winkel - 0.45), y2 - spitze * Math.sin(winkel - 0.45));
      ctx.lineTo(x2 - spitze * Math.cos(winkel + 0.45), y2 - spitze * Math.sin(winkel + 0.45));
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.shadowBlur = 0;
}

export function Markierer({ foto, fertig, abbrechen }: { foto: Foto; fertig: (f: Foto) => void; abbrechen: () => void }) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const [bild, setBild] = useState<HTMLImageElement | null>(null);
  const [formen, setFormen] = useState<Zeichen[]>(foto.markierung ?? []);
  const [jetzt, setJetzt] = useState<Zeichen | null>(null);
  const [werkzeug, setWerkzeug] = useState<Werkzeug>("pfeil");
  const [farbe, setFarbe] = useState(FARBEN[0]!);

  useEffect(() => {
    const i = new Image();
    i.onload = () => setBild(i);
    i.src = `/api/fotos/${foto.id}/unmarkiert`;
  }, [foto.id]);

  useEffect(() => {
    const c = leinwand.current;
    if (!c || !bild) return;
    c.width = bild.naturalWidth;
    c.height = bild.naturalHeight;
    const ctx = c.getContext("2d")!;
    ctx.drawImage(bild, 0, 0);
    for (const z of jetzt ? [...formen, jetzt] : formen) zeichnen(ctx, c.width, c.height, z);
  }, [bild, formen, jetzt]);

  const punkt = (e: React.PointerEvent): [number, number] => {
    const r = leinwand.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const runter = (e: React.PointerEvent) => {
    leinwand.current!.setPointerCapture(e.pointerId);
    const p = punkt(e);
    setJetzt({ art: werkzeug, farbe, punkte: [p, p] });
  };
  const ziehen = (e: React.PointerEvent) => {
    if (!jetzt) return;
    const p = punkt(e);
    setJetzt({ ...jetzt, punkte: jetzt.art === "linie" ? [...jetzt.punkte, p] : [jetzt.punkte[0]!, p] });
  };
  const hoch = () => {
    if (!jetzt) return;
    const [a, b] = [jetzt.punkte[0]!, jetzt.punkte[jetzt.punkte.length - 1]!];
    // Ein Tipp ohne Weg ist kein Pfeil: sonst bleibt ein Punkt stehen, den niemand wollte.
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) > 0.01) setFormen([...formen, jetzt]);
    setJetzt(null);
  };

  const speichern = useMutation({
    mutationFn: () => api<Foto>("PUT", `/fotos/${foto.id}/markierung`, {
      formen, bild: formen.length ? leinwand.current!.toDataURL("image/jpeg", 0.85) : "data:image/jpeg;base64,",
    }),
    onSuccess: fertig,
  });

  return (
    <div className="markierer">
      <div className="markier-leiste">
        <div className="wahl" role="group" aria-label="Werkzeug">
          <button className={werkzeug === "pfeil" ? "an" : ""} title="Pfeil" onClick={() => setWerkzeug("pfeil")}><ArrowUpRight size={16} /></button>
          <button className={werkzeug === "kreis" ? "an" : ""} title="Kreis" onClick={() => setWerkzeug("kreis")}><Circle size={16} /></button>
          <button className={werkzeug === "linie" ? "an" : ""} title="Freihand" onClick={() => setWerkzeug("linie")}><PencilSimple size={16} /></button>
        </div>
        <div className="wahl" role="group" aria-label="Farbe">
          {FARBEN.map((f) => (
            <button key={f} className={farbe === f ? "an" : ""} aria-label={`Farbe ${f}`} onClick={() => setFarbe(f)}>
              <span className="farbpunkt" style={{ background: f }} />
            </button>
          ))}
        </div>
        <button className="knopf leise symbol" aria-label="Letzte Form zurücknehmen" disabled={!formen.length}
                onClick={() => setFormen(formen.slice(0, -1))}><ArrowArcLeft size={16} /></button>
        <button className="knopf leise symbol" aria-label="Alle Markierungen entfernen" disabled={!formen.length}
                onClick={() => setFormen([])}><Trash size={16} /></button>
      </div>
      <canvas ref={leinwand} className="markier-leinwand" onPointerDown={runter} onPointerMove={ziehen}
              onPointerUp={hoch} onPointerCancel={() => setJetzt(null)} />
      {!bild && <div className="leer">Foto lädt …</div>}
      <Meldung fehler={speichern.error} />
      <div className="markier-leiste" style={{ justifyContent: "flex-end" }}>
        <button className="knopf leise" onClick={abbrechen}>Abbrechen</button>
        <button className="knopf haupt" disabled={!bild || speichern.isPending} onClick={() => speichern.mutate()}>
          Markierung speichern</button>
      </div>
    </div>
  );
}
